import cx from 'classnames';
import get from 'lodash.get';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getProgressReportConfig } from './config';
import getLegendText from './functions/getLegendText';
import ProgressChartLatestScore from './ProgressChartLatestScore';
import ProgressChart from './ProgressChart';
import ProgressChartScoreExplain from './ProgressChartScoreExplain';
import ProgressChartTrendLineLegend from './ProgressChartTrendLineLegend';
import ProgressChartBenchmarkLegend from './ProgressChartBenchmarkLegend';
import ProgressTable from './ProgressTable';
import GrowthBox from '../../../containers/Progress/GrowthBox';
import { isEarlyReader } from "../../../../../services/EarlyReaderService";
import cloneDeep from 'lodash.clonedeep';
import shouldShowChart from './functions/shouldShowChart';
import WithNavigate from '../../../../HOC/WithNavigate';

class ProgressReportClassComponent extends React.Component {
  constructor(props) {
    super(props);
    this.onChartClick = this.onChartClick.bind(this);
  }

  // When an activity is clicked, navigate to it's page
  onChartClick(e) {
    if (e.point.activityId)
      this.props.navigate('/teacher/gradebook/scoring/' + e.point.activityId);
  }

  loadReport() {
    const reportData = cloneDeep(this.props?.report)

    if (reportData?.scale && reportData.scale !== 'ARM') {
        reportData.data.forEach((obj) => {
          if (isEarlyReader(obj)) {
            obj.score = null;
          }
        });
    }

    // rendering arm score only
    if (!reportData.data[0]) return {}

    const translatedReportData = {
      ...reportData,
      label: this.props.t(reportData.label),
      locale: this.props.i18n.language,
    }

    const { t, embedded, selectedEquating, customBenchmarks, selectedBenchmark, uiConfig = { isYAxisShown: true, isYearShown: true, isLegendShown: true, hasSpasing: true } } = this.props
    const isDistrictBenchmark = get(selectedBenchmark, "value", "").toLowerCase().includes('district')
    const legendText = getLegendText(selectedBenchmark, customBenchmarks, t)
    const config = getProgressReportConfig(
      translatedReportData,
      this.onChartClick,
      embedded,
      true,
      legendText,
      isDistrictBenchmark,
      selectedEquating,
      t,
      uiConfig,
    )
    return config || {}
  }

  render() {
    const { t, embedded, small, selectedLocale, selectedEquating, report, customBenchmarks, selectedBenchmark, notEarlyReaderCount, armScore, progressChartRef, selectedStatePercentileCuts, i18n } = this.props
    if (!report) return null;

    // search for "ARM SPANISH SCORES" to find other places where arm score is determined.
    const reportArmScore = report.armScore

    const reportConfig = this.loadReport();
    const scoreLabel = report.label === "ESRI" ? "EHFWI" : report.label;
    const showChart = shouldShowChart(report, selectedLocale)

    const progressStyles = cx({
      progressChart: true,
      embedded: embedded,
      rawScore: !selectedEquating
    })

    return <React.Fragment>
      <GrowthBox
        scale={report.scale}
        data={report.data}
        embedded={embedded}
        isPending={report.isPending} 
        selectedLocale={this.props.selectedLocale}
        selectedBenchmark={selectedBenchmark}
        firstName={report.data?.[0]?.firstName} />
      <div className={progressStyles}>
        <ProgressChartLatestScore
          shouldShowChart={showChart}
          embedded={embedded}
          small={small}
          selectedEquating={selectedEquating}
          latestScore={armScore || reportArmScore} //if we have an armScore through props, use it instead of the one in the report. This is used when the progress report is embedded in the parent report
          label={armScore ? scoreLabel : "ARM"} />
        <React.Fragment>
          <ProgressChart
            shouldShowChart={showChart}
            embedded={embedded}
            reportConfig={reportConfig}
            notEarlyReaderCount={notEarlyReaderCount}
            progressChartRef={progressChartRef}
            i18n={i18n}
          />
          <ProgressChartScoreExplain
            embedded={embedded}
            shouldShowChart={showChart}
            scoreLabel={scoreLabel} />
          <div className="progressLegend">
            <ProgressChartTrendLineLegend
              shouldShowChart={showChart}
              embedded={embedded}
              selectedEquating={selectedEquating}
              trendline={report.trendline}
              trend={report.trend}
              label={scoreLabel} />
            <ProgressChartBenchmarkLegend
              shouldShowChart={showChart}
              poorCutoff={report.poorCutoff}
              averageCutoff={report.averageCutoff}
              goodCutoff={report.goodCutoff}
              embedded={embedded}
              selectedEquating={selectedEquating}
              selectedBenchmark={selectedBenchmark}
              customBenchmarks={customBenchmarks}
              selectedStatePercentileCuts={selectedStatePercentileCuts}
            />
          </div>
          <ProgressTable
            embedded={embedded}
            report={report}
            onChartClick={this.onChartClick}
            selectedLocale={this.props.selectedLocale}
            selectedMetric={this.props.selectedMetric}
          />
        </React.Fragment>
      </div>
    </React.Fragment>
  }
}
const ProgressReport = props => {
  const { t, i18n } = useTranslation('teacher_report_chart_progress');
  return <ProgressReportClassComponent {...props} t={t} i18n={i18n} />
};

export default WithNavigate(ProgressReport);